import { useMemo } from 'react';

import { useQuery } from '@hooks/reactQuery';

import { getAssetsRentRequestsDocuments } from '@api/assetsRentDocuments';

import { TPartialSort } from '@interfaces/components/table';
import { IGetAssetRequestDocumentsDTO } from '@interfaces/rent/dto/assetRentDocumentsDTO';

import { refetchOptions } from '@constants/common';
import { ASSET_REQUEST_DOCUMENTS } from '@constants/queryKeys';
import {
	EAttachedDocumentsColumnsAccessors,
} from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/AttachedContractDocuments/constants';

interface IUseQueryAssetRequestDocumentsParams extends TPartialSort, IGetAssetRequestDocumentsDTO {
	isDocumentsAccess?: boolean;
}

export const useQueryAssetRequestDocuments = (params: IUseQueryAssetRequestDocumentsParams) => {
	const {
		isAo,
		requestId,
		sortFieldName = EAttachedDocumentsColumnsAccessors.uploaded,
		sortDirection = false,
		isDocumentsAccess,
	} = params;

	const {
		data,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [ASSET_REQUEST_DOCUMENTS, requestId],
		queryFn: () => {
			return getAssetsRentRequestsDocuments({
				isAo,
				requestId,
			});
		},
		enabled: [requestId, isDocumentsAccess],
		...refetchOptions,
	});

	const sortedDocuments = useMemo(() => {
		if (!data) return [];

		return [...data].sort((a, b) => {
			const [first, second] = sortDirection ? [a, b] : [b, a];

			switch (sortFieldName) {
				case EAttachedDocumentsColumnsAccessors.file:
					return first.file?.name.localeCompare(second.file?.name);
				case EAttachedDocumentsColumnsAccessors.uploaded:
					return first.createdAt - second.createdAt;
				default:
					return 0;
			}
		});
	}, [data, sortFieldName, sortDirection]);

	return {
		requestDocuments: sortedDocuments || [],
		count: data?.length || 0,
		isLoadingRequestDocuments: (isLoading && !!data) || isFetching,
	};
};
